import queryString from 'query-string'

export const RECOMMENDATIONS_COLLECTION_HANDLE = 'rch'
export const RECOMMENDATIONS_ES_COLLECTION_HANDLE = 'rech'
export const TRACK_CHANNEL = '_channel'
export const OPTION_COLOR_SEPARATOR = '__SPU__'

// products whose category belongs to electronics and nails has no size chart
export const productTypeWithoutSizeCart = ['Phone Cases', 'Android Phone Cases', 'Airpods Cases', 'Earphones', 'Kindle Cover', 'Electronics::Accessories', 'Nails']

// get all variants which are related to current variant option
export const getVariantsByVariantOptions = (options, variants) => {
  const keys = Object.keys(options)
  if (keys.length <= 0) {
    return []
  }

  return variants.filter(variant => {
    const selectedOptionMap = {}
    variant.selectedOptions.forEach(selectedOption => {
      selectedOptionMap[selectedOption.name] = selectedOption.value
    })

    let isSelected = true
    for (let option in options) {
      if (!(selectedOptionMap[option] && selectedOptionMap[option] === options[option])) {
        isSelected = false
      }
    }
    return isSelected
  })
}


/**
 * 在 variations 中找到与 selectedVariantInfo 完全一致的 SKU：
 * selectedOptions 条数必须与 selectedVariantInfo 的键数量相同，且 name/value 一一对应。
 * 若只选了部分维度（例如 2 项）而变体有 4 项 selectedOptions，则不算匹配。
 */
export const getExactMatchingVariant = (selectedVariantInfo, variations) => {
  if (!selectedVariantInfo || !variations || variations.length === 0) {
    return undefined
  }

  const keys = Object.keys(selectedVariantInfo)
  if (keys.length === 0) {
    return undefined
  }

  for (let i = 0; i < variations.length; i++) {
    const variant = variations[i]
    const selectedOptions = variant.selectedOptions || []
    if (selectedOptions.length !== keys.length) {
      continue
    }

    const selectedOptionMap = {}
    selectedOptions.forEach(opt => {
      selectedOptionMap[opt.name] = opt.value
    })

    // 条数已一致时，只需核对「selectedVariantInfo 的每个键」与 map 一致即可；
    // 不可能出现 map 多出的 name 而不被 keys 覆盖（否则长度会对不上或某 key 对不上）。
    const allMatch = keys.every(name => selectedOptionMap[name] === selectedVariantInfo[name])
    if (allMatch) {
      return variant
    }
  }

  return undefined
}

export const isVariantsDisabledByVariantOptions = (options, variants) => {
  const vars = getVariantsByVariantOptions(options, variants) // 获取该option关联的所有sku，例如options: {Color: 'Red'}
  let isAvailable = false
  vars.forEach(variant => {
    if (variant.availableForSale) { // 只要有一个sku可售，即该option就不是disabled
      isAvailable = true
    }
  })
  return !isAvailable
}


export const getProductDetailTrackChannel = location => {
  let channel

  if (location && location.search) {
    const query = queryString.parse(location.search)

    channel = query[TRACK_CHANNEL]
  }

  return channel || ''
}

export const setDefaultSelectedOptionValues = (product) => {
  let currentVariantInfo = {}
  const { variations = [], options, handle } = product
  const availableVariations = variations.filter(i => i.availableForSale)

  if (availableVariations.length === 1) { // 单 sku
    const { selectedOptions } = availableVariations[0]

    selectedOptions.forEach(i => {
      currentVariantInfo[i.name] = i.value
    })
  } else if (options.length >= 2) {
    let colorOption = {}, fitOption = {}, inseamOption = {}

    options.forEach(i => {
      if (i.name === 'Color') {
        colorOption = i
      } else if (i.name === 'Fit') {
        fitOption = i
      } else if (i.name === 'Inseam') {
        inseamOption = i
      }
    })

    const colorOptionValues = (colorOption.values || []).filter(value => {
      const optionHandle = value.split(OPTION_COLOR_SEPARATOR)[1]

      if (!optionHandle || handle === optionHandle) {
        return true
      } else {
        return false
      }
    })

    for (let i = 0; i < colorOptionValues.length; i++) {
      const disabled = isVariantsDisabledByVariantOptions({
        [colorOption.name]: colorOptionValues[i]
      }, variations)

      if (!disabled) {
        currentVariantInfo[colorOption.name] = colorOptionValues[i]
        break
      }
    }

    // fit option default value
    for (let i = 0; i < (fitOption.values || []).length; i++) {
      const disabled = isVariantsDisabledByVariantOptions({
        ...currentVariantInfo,
        [fitOption.name]: fitOption.values[i]
      }, variations)

      if (!disabled) {
        currentVariantInfo[fitOption.name] = fitOption.values[i]
        break
      }
    }

    // inseam option default value
    for (let i = 0; i < (inseamOption.values || []).length; i++) {
      const disabled = isVariantsDisabledByVariantOptions({
        ...currentVariantInfo,
        [inseamOption.name]: inseamOption.values[i]
      }, variations)

      if (!disabled) {
        currentVariantInfo[inseamOption.name] = inseamOption.values[i]
        break
      }
    }
  }
  return currentVariantInfo
}

export const getPureVariantInfo = variantInfo => {
  if (!variantInfo) {
    return variantInfo
  } else {
    if (variantInfo.Color) {
      return {
        ...variantInfo,
        Color: variantInfo.Color.split(OPTION_COLOR_SEPARATOR)[0]
      }
    } else {
      return variantInfo
    }
  }
}


export const getPriceList = props => data => {
  const {
    appliedCoupon,
    discountTag,
    discountFee,
    freight,
    displayFreight,
    displayDiscountFee,
    displayProductFee,
    displayTotalFee,
    displayTotalSaveFee,
    totalSaveFee,
    checkoutItemCount,
    coinsFee,
    deductCoin,
    displayCoinsFee,
    couponName
  } = data

  const { showSubTotal, showDiscount } = props

  const priceList = []

  // line items is durning user update on local site, cannot calculate real price at this time
  if (checkoutItemCount > 0) {
    if (showSubTotal) {
      priceList.push({
        title: props.$i18n.transl('core.checkout.subtotal'),
        value: displayProductFee
      })
    } else {
      priceList.push({
        title: checkoutItemCount > 1 ? `${checkoutItemCount} ${props.$i18n.transl('core.checkout.items')}` : `${checkoutItemCount} ${props.$i18n.transl('core.cart.item')}`,
        value: displayProductFee
      })
    }

    // discount
    if (discountFee > 0 || showDiscount) {
      let couponTitle


      if (appliedCoupon && appliedCoupon.meta) {
        couponTitle = appliedCoupon.meta.title
      } else if (couponName) {
        couponTitle = couponName
      }

      const discountItem = {
        title: !!discountTag ? couponTitle : props.$i18n.transl('core.checkout.discount'),
        value: `${displayDiscountFee}`,
        negative: true,
        classKey: 'discount',
        subTitle: !discountTag ? ` (${couponTitle})` : '',
        tag: discountTag
      }

      priceList.push(discountItem)
    }


    // coinsFee
    if (coinsFee > 0) {
      let subTitle = ''
      if (deductCoin) {
        subTitle = `(-${props.$i18n.transl('core.checkout.coinsNum', {
          count: parseInt(deductCoin).toLocaleString()
        })})`
      }

      priceList.push({
        title: props.$i18n.transl('core.checkout.coinsExchange'),
        value: displayCoinsFee,
        subTitle,
        negative: true
      })
    }


    priceList.push({
      title: props.$i18n.transl('core.checkout.shippingFee'),
      value: freight > 0 ? displayFreight : props.$i18n.transl('core.checkout.shippingFeeFree'),
      classKey: 'shippingFee'
    })
  }

  if (!props.hideTotal) {
    priceList.push({
      title: props.$i18n.transl('core.checkout.total'),
      value: displayTotalFee,
      classKey: 'orderTotal'
    })
  }

  if (totalSaveFee && totalSaveFee !== 0) {
    priceList.push({
      title: props.$i18n.transl('core.cart.youSave'),
      value: displayTotalSaveFee,
      classKey: 'totalSaveFee'
    })
  }



  return priceList
}